import { Injectable, Injector } from '@angular/core';
import { Observable, Subscriber, TimeoutError } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

// Timeout do POST de login. O Firebase já tem seu próprio timeout
// (auth.service); aqui protegemos a etapa HTTP para o botão "Entrando..."
// não travar indefinidamente se o backend não responder (GESTOR-CLIENT-TW).
const LOGIN_HTTP_TIMEOUT_MS = 15000;
const LOGOUT_HTTP_TIMEOUT_MS = 15000;

import { ApplicationApiService } from 'src/app/shared/services/application-api.service';
import { AuthAttemptTrackerService } from 'src/app/core/services/auth-attempt-tracker.service';
import { BroadcasterService } from 'src/app/core/services/broadcaster.service';
import { SessionService, TOKEN_NAME, LAST_LOGIN } from 'src/app/shared/services/session.service';
import { CrossDomainStorageService } from 'src/app/core/services/cross-domain-storage.service';
import { InactiveCheckService } from 'src/app/core/services/inactive-check.service';
import { AuthService } from 'src/app/core/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService extends ApplicationApiService {
  private logged: boolean;
  private logoutInProgress = false;

  constructor(
    private authService: AuthService,
    private attemptTracker: AuthAttemptTrackerService,
    private sessionService: SessionService,
    private storageService: CrossDomainStorageService,
    private broadcasterService: BroadcasterService,
    private inactiveCheckService: InactiveCheckService,
    protected injector: Injector
  ) {
    super(injector);
    this.logged = false;
  }

  public lastLogin(): Promise<string> {
    return this.sessionService.lastLogin();
  }

  // Instrumentação do fluxo de login (GESTOR-CLIENT-TW/TX). Registra cada
  // etapa como breadcrumb do Sentry: se o fluxo travar sem lançar erro, o
  // último breadcrumb registrado indica exatamente onde parou. Os passos do
  // Firebase em background ficam em AuthService (firebase-bg:*).
  private logLoginStep(step: string, data?: Record<string, any>): void {
    console.debug(`[login-flow] ${step}`, data ?? '');
    Sentry.addBreadcrumb({
      category: 'login-flow',
      message: step,
      level: 'info',
      data,
    });
  }

  public login(username: string, password: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      // Uma nova tentativa de login inicia uma nova sessão lógica. O término
      // tardio do DELETE de logout anterior não pode mais alterar este estado.
      this.logoutInProgress = false;
      this.logLoginStep('auth-api:http:calling');
      this.http.post<{ token?: string }>('/api/accounts/auth/login/', { username, password }).pipe(
        timeout(LOGIN_HTTP_TIMEOUT_MS)
      ).subscribe(res => {
        this.logLoginStep('auth-api:http:resolved');
        const token = this.setToken(res?.token);
        this.inactiveCheckService.reset();
        this.storageService.setItem(LAST_LOGIN, btoa(username));

        if (!token) {
          this.logged = false;
          this.handlerError(observer, 'Token de autenticação inválido ou não recebido.');
          return;
        }

        this.sessionService.setLastLogin(username);
        this.attemptTracker.recordSuccessfulAttempt(username);

        // Sessão Firebase FORA do caminho crítico (GESTOR-CLIENT-TX): o gate
        // de "logado" é o token do backend; o signIn do Firebase roda em
        // background com retry próprio e nunca segura o botão "Entrando...".
        this.authService.signInInBackground(username, password);

        this.logged = true;
        this.handlerNext(observer, true);
      }, (err) => {
        this.logLoginStep('auth-api:http:error', { status: err?.status });
        this.logged = false;

        if (err instanceof TimeoutError) {
          this.handlerError(
            observer,
            'O servidor demorou a responder. Verifique sua conexão e tente novamente.'
          );
        } else if (err.status >= 400 && err.status < 500) {
          // Força bruta é medida no gate real (4xx do backend); fire-and-forget
          // para nunca segurar o fluxo do botão.
          this.attemptTracker.trackFailure(username);
          this.handlerError(observer, 'Usuário e/ou senha inválidos.');
        } else {
          this.handlerError(observer, 'Ocorreu um erro ao realizar login!');
        }
      });
    });
  }

  public logout(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (this.logoutInProgress) {
        this.handlerNext(observer, false);
        return;
      }

      // A inscrição ocorre antes da limpeza para o interceptor capturar o token
      // da sessão que está sendo encerrada. A UI e o estado local, porém, não
      // aguardam rede/Firebase: além de evitar travamento pós-idle, isso impede
      // que a resposta tardia deste DELETE apague um token criado por relogin.
      const logoutRequest = this.http.delete('/api/accounts/auth/logout/').pipe(
        timeout(LOGOUT_HTTP_TIMEOUT_MS)
      ).subscribe(res => {
        observer.next(res !== null);
        observer.complete();
      }, () => {
        observer.next(false);
        observer.complete();
      });

      this.endLocalSession();

      return () => logoutRequest.unsubscribe();
    });
  }

  /** Encerra localmente uma sessão que o backend informou estar inválida. */
  public expireSession(): void {
    this.endLocalSession();
  }

  private endLocalSession(): void {
    if (this.logoutInProgress) {
      return;
    }

    this.logoutInProgress = true;
    this.broadcasterService.broadcast('logout');
    this.clear();
    this.inactiveCheckService.reset();
    this.authService.logout();
  }

  public requestChangePassword(email: string, recaptcha?: string): Observable<any[]> {
    return this.http.post<any[]>(`/api/accounts/auth/user/request-change-pwd/`, { email, recaptcha }).pipe(
      map((res) => res)
    );
  }

  public changePassword(obj): Observable<any[]> {
    return this.http.post<any[]>(`/api/accounts/auth/user/change-pwd/`, obj).pipe(
      map(res => res)
    );
  }

  public join(obj): Observable<any> {
    return this.http.post(`/api/accounts/auth/join/${obj.invite_code}/`, obj).pipe(
      map(res => res)
    );
  }

  public register(obj): Observable<any> {
    return this.http.post(`/api/accounts/auth/register/${obj.invite_code}/`, obj).pipe(
      map(res => res)
    );
  }

  public SetLogged(logged: boolean) {
    this.logged = logged;
  }

  public isLogged(): boolean {
    return this.logged;
  }

  public clear() {
    this.logged = false;
    this.storageService.removeItem(TOKEN_NAME);
    // this.inactiveCheckService.reset();
    this.sessionService.clear();
  }

  private setToken(token) {
    this.storageService.setItem(TOKEN_NAME, token);
    return !token ? null : token;
  }

  private handlerNext(observer: Subscriber<any>, value: any) {
    observer.next(value);
    observer.complete();
  }

  private handlerError(observer: Subscriber<any>, value: any) {
    observer.error(value);
    observer.complete();
  }
}
